/**
 * 客户详情「档案与背景」下半部分：每本书（订单）一份讲述人与背景。
 *
 * 一位客户给爸妈各开一本时就是两个面板，各自保存、互不影响；订单任何状态都能改——
 * 销售通常付款以后才陆续录背景。保存走 PATCH /advisor/orders/:id/profile（名下校验，审计不记内容）。
 * 「问题清单与进度」里「从背景拼一条」读的就是这里所选那本书的背景。
 */
import { useEffect, useMemo } from 'react';
import { Alert, Button, Collapse, Empty, Form, Space, Tag, Typography, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import { updateAdvisorOrderProfile, type AdvisorOrderDto } from '../../../api/advisor';
import {
  BookProfileFields,
  bookProfileToPayload,
  orderToBookFormValues,
  type BookProfileFormValues,
} from './profile-form';
import { narratorText } from './narrator';
import { ORDER_VIEW_META } from './order-view';

const { Text } = Typography;

/** 已取消的单没有书，不出面板；其余按开单先后排，第一本在最上面 */
export function booksOf(orders: AdvisorOrderDto[]): AdvisorOrderDto[] {
  return orders.filter((o) => o.view !== 'cancelled').sort((a, b) => a.createdAt - b.createdAt);
}

function BookProfileForm({ customerId, order }: { customerId: string; order: AdvisorOrderDto }) {
  const [form] = Form.useForm<BookProfileFormValues>();
  const queryClient = useQueryClient();

  // 面板第一次展开时才挂载，挂载后再灌值；订单刷新（保存后重新拉详情）时同步
  useEffect(() => {
    form.setFieldsValue(orderToBookFormValues(order));
  }, [order, form]);

  const saveMutation = useMutation({
    mutationFn: (values: BookProfileFormValues) =>
      updateAdvisorOrderProfile(order.advisorOrderId, bookProfileToPayload(values)),
    onSuccess: () => {
      message.success('已保存');
      queryClient.invalidateQueries({ queryKey: ['advisor-customer', customerId] });
      queryClient.invalidateQueries({ queryKey: ['advisor-customers'] });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '保存失败');
    },
  });

  return (
    <Form
      // 唯一 name：字段 id 带上订单号。爸妈两本书的表单同时挂在页面上，id 重复会让点第二本的标签聚焦到第一本的输入框
      name={`book_${order.advisorOrderId}`}
      form={form}
      layout="vertical"
      initialValues={orderToBookFormValues(order)}
      onFinish={(v) => saveMutation.mutate(v)}
    >
      <BookProfileFields />
      <PermissionGate point={PERMISSIONS.ADVISOR_CUSTOMER_WRITE}>
        <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
          保存这本书的档案
        </Button>
      </PermissionGate>
    </Form>
  );
}

export function BookProfilePanels({ customerId, orders }: { customerId: string; orders: AdvisorOrderDto[] }) {
  const books = useMemo(() => booksOf(orders), [orders]);

  if (books.length === 0) {
    return <Empty description="开单后在这里填每本书的讲述人和背景；给爸妈各做一本就各开一单" />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="一单一书：每本书各有一份讲述人和背景，互不影响。「问题清单与进度」里「从背景拼一条」用的是你选中的那本书的背景。"
      />
      <Collapse
        defaultActiveKey={[books[0].advisorOrderId]}
        items={books.map((o, i) => ({
          key: o.advisorOrderId,
          label: (
            <Space size={8} wrap>
              <Text strong>{narratorText(o.narratorName, o.narratorRelation) || `第 ${i + 1} 本（未填讲述人）`}</Text>
              <Tag color={ORDER_VIEW_META[o.view].color}>{ORDER_VIEW_META[o.view].label}</Tag>
              <Text type="secondary">{o.productTitle}</Text>
            </Space>
          ),
          children: <BookProfileForm customerId={customerId} order={o} />,
        }))}
      />
    </Space>
  );
}
